import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUpload } from "@/components/ui/FileUpload";
import { Button } from "@/components/ui/button";
import { SmartIcon } from "@/components/ui/SmartIcon";
import { Bot, Loader2, Play } from "lucide-react";

export interface FormField {
  label: string;
  type: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  options?: string[];
}

interface AssistantInputFormProps {
  bannerUrl?: string | null;
  iconUrl?: string | null;
  title?: string | null;
  description?: string | null;
  formFields: FormField[];
  formData: Record<string, any>;
  onFieldChange: (label: string, value: any) => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

export function AssistantInputForm(props: AssistantInputFormProps) {
  const {
    bannerUrl,
    iconUrl,
    title,
    description,
    formFields,
    formData,
    onFieldChange,
    onGenerate,
    isGenerating,
  } = props;

  const renderFormField = (field: FormField, index: number) => {
    const value =
      formData[field.label] ||
      (field.type === "file" || field.type === "image" ? [] : "");

    return (
      <div key={index} className="space-y-3">
        <div className="space-y-1">
          <Label
            htmlFor={field.label}
            className="text-sm font-medium flex items-center gap-1"
          >
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.description && (
            <div className="text-xs text-gray-500">{field.description}</div>
          )}
        </div>

        {field.type === "textarea" ? (
          <Textarea
            id={field.label}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => onFieldChange(field.label, e.target.value)}
            className="min-h-[100px] resize-none mt-2"
          />
        ) : field.type === "select" && field.options ? (
          <Select
            value={value}
            onValueChange={(newValue) => onFieldChange(field.label, newValue)}
          >
            <SelectTrigger className="mt-2">
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((option, optionIndex) => (
                <SelectItem key={optionIndex} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : field.type === "file" ? (
          <FileUpload
            onFilesChange={(files) => onFieldChange(field.label, files)}
            maxFiles={5}
            value={value}
            mode="file"
            className="mt-2"
          />
        ) : field.type === "image" ? (
          <FileUpload
            onFilesChange={(files) => onFieldChange(field.label, files)}
            maxFiles={5}
            value={value}
            mode="image"
            className="mt-2"
          />
        ) : (
          <Input
            id={field.label}
            type={field.type === "email" ? "email" : "text"}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => onFieldChange(field.label, e.target.value)}
            className="mt-2"
          />
        )}
      </div>
    );
  };

  return (
    <div className="min-w-[500px] w-[500px] bg-white border-r border-gray-200 flex flex-col">
      <div className="flex-1 p-6 space-y-6 overflow-y-auto">
        {/* Banner and content area */}
        <div className="relative -mt-6 mb-6">
          {/* Banner - full width */}
          <div className="-mx-6">
            {bannerUrl ? (
              <div
                className="h-32 w-full bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${bannerUrl})` }}
              />
            ) : (
              <div className="h-32 w-full bg-gradient-to-r from-gray-500 to-gray-600" />
            )}
          </div>

          {/* Icon overlapping banner - mid-cut effect */}
          <div className="absolute left-0" style={{ top: "98px" }}>
            <div
              className="bg-white rounded-full"
              style={{ padding: "0.15rem" }}
            >
              <SmartIcon
                iconUrl={iconUrl || undefined}
                size={60}
                fallbackIcon={<Bot className="h-4 w-4" />}
                alt={`${title || "Assistant"} icon`}
              />
            </div>
          </div>

          {/* Assistant Info - aligned with form fields */}
          <div className="pt-12">
            <h2 className="text-lg font-semibold text-gray-900 mb-1 truncate">
              {title || "Untitled Assistant"}
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {description || "No description available"}
            </p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-6 pb-20">
          {formFields.length > 0 ? (
            <div className="space-y-6">{formFields.map(renderFormField)}</div>
          ) : (
            <div className="text-center py-8">
              <Bot className="h-8 w-8 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                No input fields configured for this assistant.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Generation Button */}
      {formFields.length > 0 && (
        <div className="p-6 border-t border-gray-200 bg-white">
          <Button
            onClick={onGenerate}
            disabled={isGenerating}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-white h-11"
            size="lg"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Generate
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
